/**
 * Color Rush: the "You're in!" moment, played over the store itself on the
 * first page after a member unlocks it.
 *
 * Loaded by snippets/jump-color-rush-head.liquid, which also adds
 * html.cr-celebrate before first paint (so the store paints under a dark veil
 * from its very first frame) and sets window.colorRushCelebration. The gate
 * navigates straight away instead of celebrating first, because a page that is
 * being left freezes on its last frame until the next one paints.
 *
 * Everything that moves here is a CSS transform or opacity, never canvas or
 * timers: the store is still busy running its own scripts while this plays,
 * and only compositor animations stay smooth through that. Tapping skips it.
 */
(function () {
  const root = document.documentElement;
  const data = window.colorRushCelebration;
  if (!data || !root.classList.contains('cr-celebrate')) return;

  const HOLD_MS = 2800;
  const LEAVE_MS = 600;
  const CONFETTI_PIECES = 70;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const random = (min, max) => min + Math.random() * (max - min);

  function element(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /* Each letter takes the logo's next colour, like the gate's title. */
  function title() {
    const heading = element('p', 'color-rush-celebrate__title');
    heading.setAttribute('aria-label', data.title);
    [...data.title].forEach((character, index) => {
      if (character === ' ') {
        heading.appendChild(element('span', 'color-rush-celebrate__space'));
        return;
      }
      const letter = element('span', 'color-rush-celebrate__letter', character);
      letter.setAttribute('aria-hidden', 'true');
      letter.style.setProperty('--i', index);
      letter.style.setProperty('--c', `var(--cr-logo-${(index % 9) + 1})`);
      heading.appendChild(letter);
    });
    return heading;
  }

  /* Two cannons from the bottom corners; every piece's path lives in custom properties. */
  function confetti() {
    const layer = element('div', 'color-rush-celebrate__confetti');
    for (let i = 0; i < CONFETTI_PIECES; i++) {
      const fromLeft = i % 2 === 0;
      const piece = element('i', 'color-rush-celebrate__piece');
      piece.style.cssText = [
        `${fromLeft ? 'left' : 'right'}: ${random(-2, 6)}vw`,
        `--x: ${(fromLeft ? 1 : -1) * random(15, 70)}vw`,
        `--y: ${-random(45, 95)}vh`,
        `--r: ${random(-720, 720)}deg`,
        `--d: ${random(1.8, 2.8)}s`,
        `--delay: ${random(0, 0.5)}s`,
        `--c: var(--cr-logo-${Math.floor(random(1, 10))})`,
        `width: ${random(6, 12)}px`,
        `height: ${random(8, 16)}px`,
        `border-radius: ${Math.random() < 0.25 ? '50%' : '2px'}`,
      ].join(';');
      layer.appendChild(piece);
    }
    return layer;
  }

  function play() {
    const overlay = element('div', 'color-rush-celebrate');
    overlay.setAttribute('role', 'status');
    if (!reducedMotion) overlay.appendChild(confetti());
    overlay.appendChild(title());
    if (data.text) overlay.appendChild(element('p', 'color-rush-celebrate__text', data.text));
    document.body.appendChild(overlay);

    let leaving = false;
    const leave = () => {
      if (leaving) return;
      leaving = true;
      root.classList.add('cr-celebrate-leaving');
      setTimeout(() => {
        overlay.remove();
        root.classList.remove('cr-celebrate', 'cr-celebrate-leaving');
      }, LEAVE_MS);
    };
    overlay.addEventListener('click', leave);
    setTimeout(leave, reducedMotion ? 1200 : HOLD_MS);
  }

  /* Start as soon as <body> exists rather than waiting for the store to finish parsing. */
  if (document.body) {
    play();
  } else {
    new MutationObserver((records, observer) => {
      if (!document.body) return;
      observer.disconnect();
      play();
    }).observe(root, { childList: true });
  }
})();
